import { Controller } from '@hotwired/stimulus'

// Client-side multi-step wizard for a single form. Shows one step at a time
// with Back / Next, a progress indicator, per-step HTML5 validation, and a
// final review step, but the form still submits ONCE to the server, so no
// backend / draft changes are needed. Without JS the whole form stays visible
// and submittable (progressive enhancement).
export default class extends Controller {
  static targets = ['step', 'dot', 'bar', 'counter', 'title', 'back', 'next', 'submit', 'reviewField', 'reviewImages']

  connect () {
    this.current = 0
    this.boundClearFieldError = this.clearFieldError.bind(this)
    this.show({ scroll: false })
  }

  next () {
    if (!this.validateStep(this.stepTargets[this.current])) return
    if (this.current < this.stepTargets.length - 1) {
      this.current += 1
      this.show()
    }
  }

  back () {
    if (this.current > 0) {
      this.current -= 1
      this.show()
    }
  }

  submit (event) {
    for (let index = 0; index < this.stepTargets.length; index += 1) {
      if (!this.validateStep(this.stepTargets[index])) {
        event.preventDefault()
        this.current = index
        this.show()
        return
      }
    }
  }

  show ({ scroll = true } = {}) {
    const total = this.stepTargets.length
    const last = total - 1

    this.stepTargets.forEach((step, index) => {
      step.classList.toggle('hidden', index !== this.current)
    })

    this.dotTargets.forEach((dot, index) => {
      dot.dataset.state = index === this.current ? 'active' : (index < this.current ? 'done' : 'todo')
    })

    if (this.hasBarTarget) {
      this.barTarget.style.width = `${((this.current + 1) / total) * 100}%`
    }
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `Step ${this.current + 1} of ${total}`
    }
    if (this.hasTitleTarget) {
      const heading = this.stepTargets[this.current].querySelector('[data-step-title]') ||
        this.stepTargets[this.current].querySelector('h2')
      this.titleTarget.textContent = heading ? heading.textContent.trim() : ''
    }

    if (this.hasBackTarget) this.backTarget.classList.toggle('hidden', this.current === 0)
    if (this.hasNextTarget) this.nextTarget.classList.toggle('hidden', this.current === last)
    if (this.hasSubmitTarget) this.submitTarget.classList.toggle('hidden', this.current !== last)

    if (this.current === last) this.fillReview()

    if (scroll) this.element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  validateStep (step) {
    if (!step) return true

    this.clearStepErrors(step)

    const fields = Array.from(step.querySelectorAll('input, select, textarea'))
      .filter((el) => this.shouldValidateField(el, step))

    let firstInvalid = null

    for (const field of fields) {
      if (!field.checkValidity()) {
        this.showFieldError(field)
        if (!firstInvalid) firstInvalid = field
      }
    }

    const fileInput = step.querySelector('[data-image-preview-target="input"]')
    if (fileInput && fileInput.files.length === 0 && !step.querySelector('[data-existing-photos]')) {
      this.note(step, 'Add at least one photo to continue.')
      if (!firstInvalid) firstInvalid = fileInput
    } else {
      this.clearNote(step)
    }

    if (firstInvalid) {
      if (firstInvalid.willValidate) {
        firstInvalid.focus({ preventScroll: true })
        firstInvalid.reportValidity()
      }
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return false
    }

    return true
  }

  shouldValidateField (field, step) {
    if (!step.contains(field) || field.disabled || field.type === 'hidden') return false
    if (field.classList.contains('sr-only')) return false
    if (!field.willValidate && !field.required) return false
    if (!this.isFieldVisible(field, step)) return false
    return true
  }

  isFieldVisible (field, step) {
    let node = field
    while (node && node !== step) {
      if (node.hidden || node.classList?.contains('hidden')) return false
      const style = window.getComputedStyle(node)
      if (style.display === 'none' || style.visibility === 'hidden') return false
      node = node.parentElement
    }
    return true
  }

  showFieldError (field) {
    field.classList.add('!border-rose-500', '!ring-rose-500/30', 'ring-2')
    field.setAttribute('aria-invalid', 'true')

    const wrapper = field.parentElement
    if (!wrapper || wrapper.querySelector('[data-wizard-field-error]')) return

    const error = document.createElement('p')
    error.className = 'mt-1.5 text-xs font-semibold text-rose-600'
    error.setAttribute('data-wizard-field-error', '')
    error.textContent = this.fieldErrorMessage(field)
    wrapper.appendChild(error)

    field.addEventListener('input', this.boundClearFieldError)
    field.addEventListener('change', this.boundClearFieldError)
  }

  fieldErrorMessage (field) {
    if (field.validity.valueMissing) {
      if (field.tagName === 'SELECT') return 'Please select an option.'
      return 'This field is required.'
    }
    if (field.validity.patternMismatch) return field.validationMessage || 'Please match the requested format.'
    return field.validationMessage || 'Please check this field.'
  }

  clearFieldError (event) {
    const field = event.target
    field.classList.remove('!border-rose-500', '!ring-rose-500/30', 'ring-2')
    field.removeAttribute('aria-invalid')
    field.parentElement?.querySelector('[data-wizard-field-error]')?.remove()
    field.removeEventListener('input', this.boundClearFieldError)
    field.removeEventListener('change', this.boundClearFieldError)
  }

  clearStepErrors (step) {
    step.querySelectorAll('[data-wizard-field-error]').forEach((el) => el.remove())
    step.querySelectorAll('[aria-invalid="true"]').forEach((el) => {
      el.classList.remove('!border-rose-500', '!ring-rose-500/30', 'ring-2')
      el.removeAttribute('aria-invalid')
    })
  }

  note (step, message) {
    const note = step.querySelector('[data-wizard-note]')
    if (!note) { window.alert(message); return }
    note.textContent = message
    note.classList.remove('hidden')
  }

  clearNote (step) {
    const note = step.querySelector('[data-wizard-note]')
    if (note) note.classList.add('hidden')
  }

  fillReview () {
    const readText = (selector) => {
      const el = this.element.querySelector(selector)
      if (!el) return ''
      if (el.tagName === 'SELECT') {
        const option = el.options[el.selectedIndex]
        return option && option.value ? option.textContent.trim() : ''
      }
      return (el.value || '').trim()
    }

    const values = {
      title: readText('#marketplace_ad_title'),
      category: readText('#marketplace_ad_platform_category_id'),
      condition: readText('#marketplace_ad_item_condition'),
      city: readText('#marketplace_ad_city'),
      price: readText('#marketplace_ad_price'),
      phone: readText('#marketplace_ad_contact_phone'),
      link: readText('#marketplace_ad_link_url'),
      shop: readText('#marketplace_ad_shop_id')
    }

    this.reviewFieldTargets.forEach((el) => {
      const key = el.dataset.reviewField
      let value = values[key]
      if (key === 'price' && value) value = `PKR ${value}`
      if (key === 'promotion') {
        const promo = this.element.querySelector('#marketplace_ad_promotion_requested, input[name="marketplace_ad[promotion_requested]"]')
        value = promo?.checked ? 'Yes, paid boost requested' : 'No'
      }
      el.textContent = (value && value.length) ? value : '-'
    })

    if (this.hasReviewImagesTarget) {
      const preview = this.element.querySelector('[data-image-preview-target="preview"]')
      const images = preview ? preview.querySelectorAll('img') : []
      this.reviewImagesTarget.innerHTML = ''

      if (!images.length) {
        this.reviewImagesTarget.innerHTML = '<p class="col-span-full text-xs text-slate-500">No photos selected.</p>'
        return
      }

      images.forEach((img) => {
        const cell = document.createElement('div')
        cell.className = 'aspect-square overflow-hidden rounded-lg border border-slate-200 bg-white ring-1 ring-slate-200/70'
        const clone = img.cloneNode(true)
        clone.className = 'h-full w-full object-cover'
        cell.appendChild(clone)
        this.reviewImagesTarget.appendChild(cell)
      })
    }
  }
}
